import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Shield, ArrowRight, RefreshCw } from 'lucide-react';
import Button from './ui/Button';
import GradientText from './ui/GradientText';
import SEO from './SEO';
import { useAuth } from '../contexts/AuthContext';

interface TwoFactorVerificationProps {
  accountId: string;
  email: string;
  onVerificationSuccess: () => void;
  onBack: () => void;
}

const TwoFactorVerification: React.FC<TwoFactorVerificationProps> = ({
  accountId,
  email,
  onVerificationSuccess,
  onBack
}) => {
  const { t } = useTranslation();
  const { verify2FA } = useAuth();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleInputChange = (index: number, value: string) => {
    if (value.length > 1) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every(digit => digit !== '') && newCode.join('').length === 6) {
      handleVerification(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };


  const handleVerification = async (verificationCode: string) => {
    setLoading(true);
    setError('');

    try {
      await verify2FA(accountId, verificationCode);
      onVerificationSuccess();
    } catch (err: any) {
      setError(err.message || t('twoFact.verification.errors.verificationFailed'));
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/accounts/resend-2fa-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('twoFact.verification.errors.resendFailed'));
      }

      setResendCooldown(60);
    } catch (err: any) {
      setError(err.message || t('twoFact.verification.errors.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-16 bg-gray-50">
      <SEO
        title="Two-Factor Authentication - Kexa Premium"
        description="Enter your 2FA code to complete your login to Kexa Premium"
        keywords="kexa 2fa, two factor authentication, login security"
      />

      <section className="py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                <GradientText variant="primary">{t('twoFact.verification.title')}</GradientText>
              </h1>
              <p className="text-gray-600">
                {t('twoFact.verification.subtitle.codeSent')}
              </p>
              <p className="font-medium text-gray-900">{email}</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                {t('twoFact.verification.form.fields.code')}
              </label>
              <div className="flex justify-center space-x-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    className="w-12 h-12 text-center text-xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    disabled={loading}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Button
                type="button"
                variant="primary"
                size="lg"
                className="w-full"
                rightIcon={<ArrowRight size={16} />}
                disabled={loading || code.some(digit => !digit)}
                onClick={() => handleVerification(code.join(''))}
              >
                {loading ? t('twoFact.verification.form.buttons.verifying') : t('twoFact.verification.form.buttons.verify')}
              </Button>

              <div className="text-center">
                <p className="text-gray-600 text-sm mb-2">
                  {t('twoFact.verification.form.messages.didNotReceive')}
                </p>
                <Button
                  onClick={handleResendCode}
                  disabled={resendLoading || resendCooldown > 0}
                  variant='ghost'
                  className='mt-4'
                >
                  <span>
                    {resendCooldown > 0
                      ? t('twoFact.verification.form.buttons.resendCooldown', { seconds: resendCooldown })
                      : resendLoading
                      ? t('twoFact.verification.form.buttons.resending')
                      : t('twoFact.verification.form.buttons.resend')
                    }
                  </span>
                </Button>
              </div>

              <div className="text-center pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onBack}
                  disabled={loading}
                >
                  {t('twoFact.verification.form.buttons.backToLogin')}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TwoFactorVerification;